import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import SectionHeader from "@/components/SectionHeader";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from "@/components/ui/accordion";
import { ArrowLeft, FileText, AlertTriangle, CheckCircle2, BookOpen, GraduationCap, HelpCircle, Lock, Heart } from "lucide-react";
import { meta } from "@/lib/peptideMeta";
import { activeVariants, defaultVariant, hasMultipleVariants } from "@/lib/variants";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const moleculeImg =
  "https://images.unsplash.com/photo-1656331797721-b593b8f00297?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxtb2xlY3VsZSUyMHN0cnVjdHVyZSUyMG1lZGljYWx8ZW58MHx8fHwxNzg0MzU5MDAzfDA&ixlib=rb-4.1.0&q=85";

const PRODUCT_FAQ = [
  { q: "Is this product for veterinary use only?", a: "Yes. All compounds listed on Peps4Pets are for veterinary and laboratory research use only. They are not for human consumption." },
  { q: "How do I access the COA?", a: "The COA is attached to every shipment and available in your customer portal by batch number." },
  { q: "What is the recommended storage?", a: "Store lyophilized product at -20°C. Once reconstituted, refrigerate at 2–8°C and use within the timeframe stated on the documentation." },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const canSeePricing = !!user && user.onboarding_complete;
  const [p, setP] = useState(null);
  const [all, setAll] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [wished, setWished] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [articles, setArticles] = useState([]);
  const { addItem } = useCart();

  useEffect(() => {
    setP(null);
    setNotFound(false);
    setWished(false);
    setSelectedVariantId(null);
    api.get(`/peptides/${slug}`).then((r) => {
      setP(r.data);
      const def = defaultVariant(r.data);
      if (def) setSelectedVariantId(def.id);
    }).catch(() => setNotFound(true));
    api.get("/peptides").then((r) => setAll(r.data));
    api.get(`/products/${slug}/research`).then((r) => setArticles(r.data || [])).catch(() => setArticles([]));
    window.scrollTo({ top: 0, behavior: "instant" });
    if (user) {
      api.post("/recently-viewed", { peptide_slug: slug }).catch(() => {});
      api.get("/wishlist").then((r) => setWished(r.data.some((w) => w.peptide_slug === slug))).catch(() => {});
    }
  }, [slug, user]);

  const toggleWish = async () => {
    if (!user) { toast.info("Sign in to save products."); return; }
    try {
      if (wished) { await api.delete(`/wishlist/${slug}`); setWished(false); toast.success("Removed from wishlist"); }
      else { await api.post("/wishlist", { peptide_slug: slug }); setWished(true); toast.success("Added to wishlist"); }
    } catch { toast.error("Wishlist update failed"); }
  };

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl">Peptide not found</h1>
        <Link to="/catalog" className="text-blue-700 mt-4 inline-block">← Back to catalog</Link>
      </div>
    );
  }
  if (!p) return <div className="py-24 text-center text-slate-400 font-mono text-xs uppercase tracking-widest">Loading compound…</div>;

  const m = meta(p.slug);
  const related = all.filter((x) => x.slug !== p.slug && x.category === p.category).slice(0, 4);
  const variants = activeVariants(p);
  const selected = variants.find((v) => v.id === selectedVariantId) || defaultVariant(p);
  const multi = hasMultipleVariants(p);
  const displayPrice = selected ? Number(selected.price || 0) : Number(p.price ?? 0);

  const facts = [
    { k: "Category", v: p.category },
    { k: "Purity", v: p.purity },
    { k: "Form", v: p.form },
    { k: "Sequence", v: p.sequence, mono: true },
    { k: "Molecular Weight", v: p.molecular_weight, mono: true },
  ];

  return (
    <div data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <Link to="/catalog" data-testid="back-to-catalog" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> Back to catalog
        </Link>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Large image */}
          <div className="lg:col-span-6">
            <div className="border border-slate-200 rounded-md overflow-hidden bg-slate-50">
              <img src={p.image_url || moleculeImg} alt={p.name} className="w-full h-[520px] object-cover" />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="border border-slate-200 rounded-md p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Batch</div>
                <div className="mt-1 font-mono text-sm text-slate-900">P4P-2026-042</div>
              </div>
              <div className="border border-slate-200 rounded-md p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Endotoxin</div>
                <div className="mt-1 font-mono text-sm text-slate-900">&lt; 0.05 EU/mg</div>
              </div>
              <div className="border border-slate-200 rounded-md p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Sterility</div>
                <div className="mt-1 text-sm text-slate-900">Pass</div>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="lg:col-span-6">
            <div className="flex items-center gap-2">
              <Badge className="rounded-md bg-blue-50 text-blue-700 border border-blue-200 font-mono text-[10px] uppercase tracking-widest">{p.category}</Badge>
              <Badge className="rounded-md bg-slate-100 text-slate-700 border border-slate-200 font-mono text-[10px] uppercase tracking-widest">Research Only</Badge>
            </div>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold text-slate-900 tracking-tight">{p.name}</h1>
            <div className="text-slate-500 mt-2">{p.chemical_name}</div>

            <div className="mt-6">
              {canSeePricing ? (
                <div className="space-y-4">
                  {multi && (
                    <div data-testid="pd-variant-selector">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                        Select dose · {variants.length} options
                      </div>
                      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Dose selection">
                        {variants.map((v) => {
                          const isSel = v.id === selected?.id;
                          const oos = (v.inventory ?? 0) <= 0;
                          return (
                            <button
                              key={v.id}
                              type="button"
                              role="radio"
                              aria-checked={isSel}
                              disabled={oos}
                              data-testid={`pd-variant-${v.id}`}
                              onClick={() => setSelectedVariantId(v.id)}
                              className={`min-w-[80px] px-4 py-2.5 rounded-md border text-left transition-colors ${
                                isSel
                                  ? "bg-slate-900 text-white border-slate-900"
                                  : oos
                                  ? "bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed"
                                  : "bg-white text-slate-900 border-slate-300 hover:border-slate-900"
                              }`}
                            >
                              <div className="font-display text-sm font-semibold">{v.label}</div>
                              <div className={`font-mono text-[11px] tabular-nums mt-0.5 ${isSel ? "text-white/80" : "text-slate-500"}`}>
                                ${Number(v.price || 0).toFixed(2)}
                                {oos && <span className="ml-1">· Out</span>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="flex items-baseline gap-3">
                    <div data-testid="pd-price" className="font-display text-4xl font-semibold text-slate-900 tabular-nums">
                      ${displayPrice.toFixed(2)}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                      {selected?.label ? `${selected.label} · per vial` : "per vial"}
                    </div>
                  </div>
                </div>
              ) : (
                <div data-testid="pd-price-locked" className="border border-blue-200 bg-blue-50/60 rounded-md p-5 flex items-start gap-3">
                  <Lock className="w-5 h-5 mt-0.5 text-blue-700 flex-shrink-0" strokeWidth={1.75} />
                  <div className="flex-1">
                    <div className="font-display text-lg font-semibold text-slate-900">{user ? "Complete your profile to view pricing" : "Sign in to view pricing"}</div>
                    <p className="mt-1 text-sm text-slate-600">
                      {user ? "Finish your one-time account setup to unlock pricing, batch history, and COA downloads." : "Create a free Peps4Pets research account to unlock pricing, batch history, and COA downloads."}
                    </p>
                    <Link to={user ? "/onboarding" : "/login"} className="inline-block mt-3">
                      <Button data-testid="pd-create-account-btn" className="h-10 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm">
                        {user ? "Complete account setup" : "Create account"}
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <p className="mt-6 text-slate-700 text-base leading-relaxed">{p.summary}</p>

            <div className="mt-8 flex flex-wrap gap-3">
              {canSeePricing ? (
                <div className="flex items-center gap-2">
                  <div className="flex items-center border border-slate-300 rounded-md h-11">
                    <button type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))} data-testid="pd-qty-decrement" className="px-3 h-full hover:bg-slate-100 text-slate-600">−</button>
                    <div className="px-4 font-mono text-sm" data-testid="pd-qty">{quantity}</div>
                    <button type="button" onClick={() => setQuantity(quantity + 1)} data-testid="pd-qty-increment" className="px-3 h-full hover:bg-slate-100 text-slate-600">+</button>
                  </div>
                  <Button data-testid="pd-add-to-cart" onClick={() => {
                    if (!selected) return;
                    addItem({
                      peptide_slug: p.slug,
                      variant_id: selected.id,
                      quantity,
                      name: p.name,
                      variant_label: selected.label,
                      unit_price: Number(selected.price || 0),
                      image_url: p.image_url,
                    });
                  }} className="h-11 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-md">
                    Add to cart{selected?.label ? ` · ${selected.label}` : ""}
                  </Button>
                </div>
              ) : (
                <Link to={user ? "/onboarding" : "/login"}>
                  <Button data-testid="pd-inquire-btn" className="h-11 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-md">
                    <Lock className="w-4 h-4 mr-2" strokeWidth={1.75} /> {user ? "Complete profile to shop" : "Sign in to shop"}
                  </Button>
                </Link>
              )}
              <Button data-testid="pd-coa-btn" variant="outline" className="h-11 px-5 border-slate-300 rounded-md">
                <FileText className="w-4 h-4 mr-2" strokeWidth={1.75} /> View COA
              </Button>
              <Button data-testid="pd-wishlist-btn" onClick={toggleWish} variant="outline" className={`h-11 px-4 rounded-md ${wished ? "border-blue-300 text-blue-700 bg-blue-50" : "border-slate-300"}`}>
                <Heart className={`w-4 h-4 mr-1.5 ${wished ? "fill-blue-600" : ""}`} strokeWidth={1.75}/> {wished ? "Saved" : "Save"}
              </Button>
            </div>

            <div className="mt-8 border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="pd-facts">
              {facts.map((f) => (
                <div key={f.k} className="grid grid-cols-[160px_1fr] px-5 py-3">
                  <div className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{f.k}</div>
                  <div className={`text-sm text-slate-900 ${f.mono ? "font-mono" : ""}`}>{f.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Research areas + Dosage + Storage */}
      <section className="border-t border-slate-200 bg-slate-50 mt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="border border-slate-200 rounded-md p-6 bg-white">
            <div className="eyebrow mb-3">Research Areas</div>
            <ul className="space-y-2">
              {p.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-blue-600" strokeWidth={1.75} /> {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-slate-200 rounded-md p-6 bg-white">
            <div className="eyebrow mb-3">Dosage Guidelines</div>
            <p className="text-sm text-slate-700 leading-relaxed">{p.dosage}</p>
          </div>
          <div className="border border-slate-200 rounded-md p-6 bg-white">
            <div className="eyebrow mb-3">Storage</div>
            <p className="text-sm text-slate-700 leading-relaxed">{p.storage}</p>
          </div>
        </div>
      </section>

      {/* Documentation section */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <SectionHeader eyebrow="Documentation" title="Every batch, fully traceable." description="Download analytical panels and browse related documentation." cta="Explore Quality standards" ctaTo="/quality" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { i: FileText, t: "Certificate of Analysis", d: "HPLC + Mass Spec assay report." },
            { i: BookOpen, t: "Handling Guide", d: "Reconstitution, storage, and shelf life." },
            { i: GraduationCap, t: "Research Notes", d: "Cited studies for this compound." },
          ].map((d) => (
            <div key={d.t} className="border border-slate-200 rounded-md p-6 bg-white card-clinical">
              <d.i className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              <div className="mt-5 font-display text-lg font-semibold text-slate-900">{d.t}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
          <SectionHeader eyebrow="FAQ" title="Common questions about this compound." align="center" />
          <Accordion type="single" collapsible className="border-t border-slate-200">
            {PRODUCT_FAQ.map((f, i) => (
              <AccordionItem key={i} value={`f-${i}`} className="border-b border-slate-200" data-testid={`pd-faq-${i}`}>
                <AccordionTrigger className="text-left text-slate-900 font-display text-lg hover:no-underline py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Related research from the CMS */}
      {articles.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <SectionHeader eyebrow="Research Hub" title="Related research articles." cta="Explore all research" ctaTo="/research" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="product-related-research">
            {articles.slice(0, 6).map((a) => (
              <Link key={a.slug} to={`/research/${a.slug}`} className="block border border-slate-200 rounded-md p-5 bg-white hover:border-blue-400 transition-colors">
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">{a.category_id || "Research"}</div>
                <div className="font-display font-semibold text-slate-900">{a.title}</div>
                {a.excerpt && <p className="mt-2 text-sm text-slate-600 line-clamp-3">{a.excerpt}</p>}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Related products */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <SectionHeader eyebrow="Related" title="Other compounds in this category." cta="View catalog" ctaTo="/catalog" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {related.map((r) => <ProductCard key={r.slug} peptide={r} />)}
          </div>
        </section>
      )}

      {/* Disclaimer */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-4 text-xs text-amber-900">
          <AlertTriangle className="w-4 h-4 mt-0.5" strokeWidth={1.75} />
          <span>Strictly for veterinary and laboratory research use. Not intended for human consumption. Not evaluated by the FDA.</span>
        </div>
      </section>
    </div>
  );
}
